import {
  IconCalendar,
  IconCheck,
  IconCircleCheck,
  IconDots,
  IconEdit,
  IconGripVertical,
  IconMailOpened,
  IconPhoto,
  IconPlus,
  IconSearch,
  IconTag,
  IconTrash,
  IconUser,
  IconUsers,
  IconX,
} from "@tabler/icons-preact";
import type { ComponentChildren } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import type {
  BoardAvatarInput,
  BoardConfigResponse,
  BoardOrderInput,
  BoardResponsible,
  BoardTag,
  BoardTagColor,
  InboxMessage,
  MessagePatchInput,
  MessageStage,
} from "../shared/contracts";
import { apiRequest } from "./api";

const STAGES: MessageStage[] = ["inbox", "attention", "waiting", "done"];

const stageLabels: Record<MessageStage, string> = {
  inbox: "Entrada",
  attention: "Em foco",
  waiting: "Aguardando",
  done: "Concluído",
};

const stageDescriptions: Record<MessageStage, string> = {
  inbox: "Ainda sem responsável",
  attention: "Exige uma próxima ação",
  waiting: "Depende de outra pessoa",
  done: "Assunto encerrado",
};

const tagColors: BoardTagColor[] = ["gray", "red", "orange", "yellow", "green", "blue", "violet"];

interface KanbanBoardProps {
  messages: InboxMessage[];
  tenantId: string;
  onOpenMessage: (id: string) => void;
  onPatchMessage: (id: string, patch: MessagePatchInput, successMessage?: string) => Promise<void>;
  onReorder: (input: BoardOrderInput) => Promise<void>;
  onToast: (message: string) => void;
}

type BoardModal = "responsible" | "deadline" | "tags";

interface MenuState {
  messageId: string;
  x: number;
  y: number;
}

interface DropTarget {
  stage: MessageStage;
  index: number;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length > 1) return `${parts[0][0]}${parts.at(-1)?.[0] ?? ""}`.toLocaleUpperCase("pt-BR");
  return (parts[0] || "PM").slice(0, 2).toLocaleUpperCase("pt-BR");
}

function localDateKey(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function shiftDate(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return localDateKey(date);
}

function deadlinePresentation(message: InboxMessage): { tone: string; label: string } {
  if (message.stage === "done") return { tone: "done", label: "Concluído" };
  if (!message.dueAt) return { tone: "none", label: "Definir prazo" };
  const dueDate = message.dueAt.slice(0, 10);
  const today = localDateKey();
  const yesterday = shiftDate(-1);
  if (dueDate > today) {
    return {
      tone: "safe",
      label: new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "short" })
        .format(new Date(`${dueDate}T12:00:00`)),
    };
  }
  if (dueDate === today) return { tone: "warning", label: "Hoje" };
  if (dueDate === yesterday) return { tone: "warning", label: "Ontem" };
  return {
    tone: "late",
    label: `Vencido ${new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "short" })
      .format(new Date(`${dueDate}T12:00:00`))}`,
  };
}

function fileToAvatar(file: File): Promise<{ avatar: BoardAvatarInput; preview: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Não foi possível ler a foto."));
    reader.onload = () => {
      const dataUrl = typeof reader.result === "string" ? reader.result : "";
      const comma = dataUrl.indexOf(",");
      if (comma < 0) {
        reject(new Error("Não foi possível ler a foto."));
        return;
      }
      resolve({
        avatar: {
          contentType: file.type as BoardAvatarInput["contentType"],
          contentBase64: dataUrl.slice(comma + 1),
        },
        preview: dataUrl,
      });
    };
    reader.readAsDataURL(file);
  });
}

function ResponsibleAvatar({ responsible, size = "small" }: { responsible: BoardResponsible; size?: "small" | "large" }) {
  return responsible.avatarUrl ? (
    <img className={`board-person-avatar board-person-avatar--${size}`} src={responsible.avatarUrl} alt="" />
  ) : (
    <span className={`board-person-avatar board-person-avatar--${size}`} aria-hidden="true">{initials(responsible.name)}</span>
  );
}

function BoardDialog({
  children,
  labelledBy,
  onClose,
}: {
  children: ComponentChildren;
  labelledBy: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div className="board-dialog-backdrop" onMouseDown={(event) => {
      if (event.currentTarget === event.target) onClose();
    }}>
      <section className="board-dialog" role="dialog" aria-modal="true" aria-labelledby={labelledBy}>
        {children}
      </section>
    </div>
  );
}

export function KanbanBoard({
  messages,
  tenantId,
  onOpenMessage,
  onPatchMessage,
  onReorder,
  onToast,
}: KanbanBoardProps) {
  const [config, setConfig] = useState<BoardConfigResponse>({ responsibles: [], tags: [] });
  const [configError, setConfigError] = useState<string | null>(null);
  const [modal, setModal] = useState<BoardModal | null>(null);
  const [modalMessageId, setModalMessageId] = useState<string | null>(null);
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  const draggedRecently = useRef(false);
  const [responsibleQuery, setResponsibleQuery] = useState("");
  const [managingResponsibles, setManagingResponsibles] = useState(false);
  const [editingResponsible, setEditingResponsible] = useState<BoardResponsible | null>(null);
  const [responsibleName, setResponsibleName] = useState("");
  const [responsibleAvatar, setResponsibleAvatar] = useState<BoardAvatarInput | undefined>();
  const [responsibleAvatarPreview, setResponsibleAvatarPreview] = useState<string | null>(null);
  const [removeResponsibleAvatar, setRemoveResponsibleAvatar] = useState(false);
  const [confirmResponsibleDelete, setConfirmResponsibleDelete] = useState<string | null>(null);
  const [responsibleFormAssign, setResponsibleFormAssign] = useState(false);
  const [dueAt, setDueAt] = useState("");
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [editingTag, setEditingTag] = useState<BoardTag | null>(null);
  const [tagName, setTagName] = useState("");
  const [tagColor, setTagColor] = useState<BoardTagColor>("gray");
  const [confirmTagDelete, setConfirmTagDelete] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const loadConfig = async () => {
    try {
      const response = await apiRequest<BoardConfigResponse>("/api/v1/board/config");
      setConfig(response);
      setConfigError(null);
    } catch (caught) {
      setConfigError(caught instanceof Error ? caught.message : "Não foi possível carregar o quadro.");
    }
  };

  useEffect(() => {
    void loadConfig();
  }, [tenantId]);

  const columns = useMemo(() => {
    const grouped: Record<MessageStage, InboxMessage[]> = {
      inbox: [],
      attention: [],
      waiting: [],
      done: [],
    };
    for (const message of messages) grouped[message.stage].push(message);
    for (const stage of STAGES) {
      grouped[stage].sort((a, b) => {
        if (a.boardPosition !== b.boardPosition) {
          if (!a.boardPosition) return 1;
          if (!b.boardPosition) return -1;
          return a.boardPosition - b.boardPosition;
        }
        return new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime();
      });
    }
    return grouped;
  }, [messages]);

  const activeMessage = modalMessageId ? messages.find((message) => message.id === modalMessageId) ?? null : null;
  const menuMessage = menu ? messages.find((message) => message.id === menu.messageId) ?? null : null;

  const closeModal = () => {
    if (busy) return;
    setModal(null);
    setModalMessageId(null);
    setResponsibleQuery("");
    setManagingResponsibles(false);
    setEditingResponsible(null);
    setConfirmResponsibleDelete(null);
    setEditingTag(null);
    setConfirmTagDelete(null);
  };

  const openResponsibleModal = (messageId: string | null, manage = false) => {
    setMenu(null);
    setModalMessageId(messageId);
    setResponsibleQuery("");
    setManagingResponsibles(manage || !messageId);
    setEditingResponsible(null);
    setResponsibleName("");
    setResponsibleAvatar(undefined);
    setResponsibleAvatarPreview(null);
    setRemoveResponsibleAvatar(false);
    setModal("responsible");
  };

  const openDeadlineModal = (message: InboxMessage) => {
    setMenu(null);
    setModalMessageId(message.id);
    setDueAt(message.dueAt?.slice(0, 10) || "");
    setModal("deadline");
  };

  const openTagsModal = (message: InboxMessage) => {
    setMenu(null);
    setModalMessageId(message.id);
    setSelectedTagIds(message.tags.map((tag) => tag.id));
    setEditingTag(null);
    setTagName("");
    setTagColor("gray");
    setModal("tags");
  };

  const updateMessage = async (messageId: string, patch: MessagePatchInput, successMessage?: string): Promise<boolean> => {
    setBusy(`message:${messageId}`);
    try {
      await onPatchMessage(messageId, patch, successMessage);
      return true;
    } catch (caught) {
      onToast(caught instanceof Error ? caught.message : "Não foi possível atualizar a conversa.");
      return false;
    } finally {
      setBusy(null);
    }
  };

  const boardInput = (nextColumns: Record<MessageStage, InboxMessage[]>): BoardOrderInput => ({
    columns: STAGES.map((stage) => ({ stage, messageIds: nextColumns[stage].map((message) => message.id) })),
  });

  const moveMessage = async (messageId: string, target: DropTarget) => {
    const sourceStage = STAGES.find((stage) => columns[stage].some((message) => message.id === messageId));
    if (!sourceStage) return;
    const sourceIndex = columns[sourceStage].findIndex((message) => message.id === messageId);
    const nextColumns = Object.fromEntries(STAGES.map((stage) => [stage, [...columns[stage]]])) as Record<MessageStage, InboxMessage[]>;
    const [message] = nextColumns[sourceStage].splice(sourceIndex, 1);
    let targetIndex = target.index;
    if (sourceStage === target.stage && sourceIndex < targetIndex) targetIndex -= 1;
    targetIndex = Math.max(0, Math.min(targetIndex, nextColumns[target.stage].length));
    nextColumns[target.stage].splice(targetIndex, 0, { ...message, stage: target.stage });
    const unchanged = sourceStage === target.stage && sourceIndex === targetIndex;
    if (unchanged) return;
    try {
      await onReorder(boardInput(nextColumns));
    } catch (caught) {
      onToast(caught instanceof Error ? caught.message : "Não foi possível reorganizar o quadro.");
    }
  };

  const moveRelative = async (message: InboxMessage, direction: "top" | "up" | "down") => {
    const current = columns[message.stage];
    const index = current.findIndex((candidate) => candidate.id === message.id);
    const targetIndex = direction === "top" ? 0 : direction === "up" ? Math.max(0, index - 1) : Math.min(current.length, index + 2);
    setMenu(null);
    await moveMessage(message.id, { stage: message.stage, index: targetIndex });
  };

  const openMenu = (messageId: string, x: number, y: number) => {
    const width = 244;
    const height = 430;
    setMenu({
      messageId,
      x: Math.max(10, Math.min(x, window.innerWidth - width - 10)),
      y: Math.max(10, Math.min(y, window.innerHeight - height - 10)),
    });
  };

  const startResponsibleForm = (responsible: BoardResponsible | null, assignAfterSave: boolean) => {
    setEditingResponsible(responsible);
    setResponsibleName(responsible?.name || responsibleQuery.trim());
    setResponsibleAvatar(undefined);
    setResponsibleAvatarPreview(responsible?.avatarUrl || null);
    setRemoveResponsibleAvatar(false);
    setResponsibleFormAssign(assignAfterSave);
  };

  const saveResponsible = async () => {
    const name = responsibleName.replace(/\s+/g, " ").trim();
    if (!name) return;
    setBusy("responsible");
    try {
      const payload = {
        name,
        ...(responsibleAvatar ? { avatar: responsibleAvatar } : {}),
        ...(removeResponsibleAvatar ? { removeAvatar: true } : {}),
      };
      const saved = await apiRequest<BoardResponsible>(
        editingResponsible
          ? `/api/v1/board/responsibles/${encodeURIComponent(editingResponsible.id)}`
          : "/api/v1/board/responsibles",
        { method: editingResponsible ? "PATCH" : "POST", body: JSON.stringify(payload) },
      );
      setConfig((current) => ({
        ...current,
        responsibles: [...current.responsibles.filter((item) => item.id !== saved.id), saved]
          .sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
      }));
      if (responsibleFormAssign && activeMessage) {
        await onPatchMessage(activeMessage.id, { responsibleId: saved.id }, `${saved.name} agora é responsável.`);
        setBusy(null);
        closeModal();
        return;
      }
      setEditingResponsible(null);
      setResponsibleName("");
      setResponsibleAvatar(undefined);
      setResponsibleAvatarPreview(null);
      setRemoveResponsibleAvatar(false);
      onToast(editingResponsible ? "Responsável atualizado." : "Responsável criado.");
    } catch (caught) {
      onToast(caught instanceof Error ? caught.message : "Não foi possível salvar o responsável.");
    } finally {
      setBusy(null);
    }
  };

  const deleteResponsible = async (responsible: BoardResponsible) => {
    setBusy(`responsible:${responsible.id}`);
    try {
      await apiRequest<void>(`/api/v1/board/responsibles/${encodeURIComponent(responsible.id)}`, { method: "DELETE" });
      setConfig((current) => ({
        ...current,
        responsibles: current.responsibles.filter((item) => item.id !== responsible.id),
      }));
      setConfirmResponsibleDelete(null);
      onToast("Responsável removido do quadro.");
    } catch (caught) {
      onToast(caught instanceof Error ? caught.message : "Não foi possível remover o responsável.");
    } finally {
      setBusy(null);
    }
  };

  const assignResponsible = async (responsibleId: string | null) => {
    if (!activeMessage) return;
    const responsible = config.responsibles.find((item) => item.id === responsibleId);
    const updated = await updateMessage(
      activeMessage.id,
      { responsibleId },
      responsible ? `${responsible.name} agora é responsável.` : "Conversa ficou sem responsável.",
    );
    if (updated) closeModal();
  };

  const saveTag = async () => {
    const name = tagName.replace(/\s+/g, " ").trim();
    if (!name) return;
    setBusy("tag");
    try {
      const saved = await apiRequest<BoardTag>(
        editingTag ? `/api/v1/board/tags/${encodeURIComponent(editingTag.id)}` : "/api/v1/board/tags",
        { method: editingTag ? "PATCH" : "POST", body: JSON.stringify({ name, color: tagColor }) },
      );
      setConfig((current) => ({
        ...current,
        tags: [...current.tags.filter((item) => item.id !== saved.id), saved]
          .sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
      }));
      if (!editingTag) setSelectedTagIds((current) => [...new Set([...current, saved.id])]);
      setEditingTag(null);
      setTagName("");
      setTagColor("gray");
      onToast(editingTag ? "Tag atualizada." : "Tag criada e selecionada.");
    } catch (caught) {
      onToast(caught instanceof Error ? caught.message : "Não foi possível salvar a tag.");
    } finally {
      setBusy(null);
    }
  };

  const deleteTag = async (tag: BoardTag) => {
    setBusy(`tag:${tag.id}`);
    try {
      await apiRequest<void>(`/api/v1/board/tags/${encodeURIComponent(tag.id)}`, { method: "DELETE" });
      setConfig((current) => ({ ...current, tags: current.tags.filter((item) => item.id !== tag.id) }));
      setSelectedTagIds((current) => current.filter((id) => id !== tag.id));
      setConfirmTagDelete(null);
      onToast("Tag removida do quadro.");
    } catch (caught) {
      onToast(caught instanceof Error ? caught.message : "Não foi possível remover a tag.");
    } finally {
      setBusy(null);
    }
  };

  const filteredResponsibles = config.responsibles.filter((responsible) =>
    responsible.name.toLocaleLowerCase("pt-BR").includes(responsibleQuery.trim().toLocaleLowerCase("pt-BR")),
  );
  const canCreateResponsible = responsibleQuery.trim().length > 0
    && !config.responsibles.some((responsible) => responsible.name.localeCompare(responsibleQuery.trim(), "pt-BR", { sensitivity: "accent" }) === 0);

  return (
    <section className="board" aria-label="Quadro de conversas">
      <header className="board__header">
        <div>
          <span>Fluxo de trabalho</span>
          <h1>Trabalho da equipe</h1>
          <p>Organize cada conversa, responsável e prazo em um só lugar.</p>
        </div>
        <button className="button button--secondary" type="button" onClick={() => openResponsibleModal(null, true)}>
          <IconUsers size={17} aria-hidden="true" />
          Gerenciar responsáveis
        </button>
      </header>

      {configError ? (
        <button className="board__config-error" type="button" onClick={() => void loadConfig()}>
          {configError} Tentar novamente
        </button>
      ) : null}

      <div className="board__scroll">
        <div className="board__columns">
          {STAGES.map((stage) => (
            <section
              className={`board-column${dropTarget?.stage === stage ? " is-drop-active" : ""}`}
              key={stage}
              aria-label={stageLabels[stage]}
              onDragOver={(event) => {
                event.preventDefault();
                if (event.target === event.currentTarget || (event.target as HTMLElement).classList.contains("board-column__empty")) {
                  setDropTarget({ stage, index: columns[stage].length });
                }
              }}
              onDrop={(event) => {
                event.preventDefault();
                if (draggedId && dropTarget) void moveMessage(draggedId, dropTarget);
                setDraggedId(null);
                setDropTarget(null);
              }}
            >
              <header className="board-column__header">
                <div><h2>{stageLabels[stage]}</h2><p>{stageDescriptions[stage]}</p></div>
                <span>{columns[stage].length}</span>
              </header>
              <div className="board-column__cards">
                {columns[stage].map((message, index) => {
                  const deadline = deadlinePresentation(message);
                  const showDropBefore = dropTarget?.stage === stage && dropTarget.index === index;
                  const showDropAfter = dropTarget?.stage === stage
                    && dropTarget.index === columns[stage].length
                    && index === columns[stage].length - 1;
                  return (
                    <article
                      className={`board-card${message.stage === "done" ? " is-completed" : ""}${draggedId === message.id ? " is-dragging" : ""}${showDropBefore ? " is-drop-before" : ""}${showDropAfter ? " is-drop-after" : ""}`}
                      key={message.id}
                      draggable
                      onDragStart={(event) => {
                        setDraggedId(message.id);
                        draggedRecently.current = true;
                        if (event.dataTransfer) {
                          event.dataTransfer.effectAllowed = "move";
                          event.dataTransfer.setData("text/plain", message.id);
                        }
                      }}
                      onDragEnd={() => {
                        setDraggedId(null);
                        setDropTarget(null);
                        window.setTimeout(() => { draggedRecently.current = false; }, 120);
                      }}
                      onDragOver={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        const rect = event.currentTarget.getBoundingClientRect();
                        setDropTarget({ stage, index: event.clientY < rect.top + rect.height / 2 ? index : index + 1 });
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (draggedId && dropTarget) void moveMessage(draggedId, dropTarget);
                        setDraggedId(null);
                        setDropTarget(null);
                      }}
                      onContextMenu={(event) => {
                        event.preventDefault();
                        openMenu(message.id, event.clientX, event.clientY);
                      }}
                    >
                      <div className="board-card__drag" aria-hidden="true"><IconGripVertical size={15} /></div>
                      <button
                        className="board-card__open"
                        type="button"
                        aria-label={`Abrir ${message.subject} em tela completa`}
                        onClick={() => {
                          if (!draggedRecently.current) onOpenMessage(message.id);
                        }}
                      >
                        <span className="board-card__labels">
                          <span className="label">{message.labels[0]}</span>
                          {message.tags.slice(0, 3).map((tag) => <span className={`board-tag board-tag--${tag.color}`} key={tag.id}>{tag.name}</span>)}
                          {message.tags.length > 3 ? <span className="board-tag board-tag--more">+{message.tags.length - 3}</span> : null}
                        </span>
                        <strong>{message.subject}</strong>
                        <p>{message.preview}</p>
                      </button>
                      <footer className="board-card__footer">
                        <button className="board-card__person" type="button" onClick={() => openResponsibleModal(message.id)}>
                          {message.responsible ? <ResponsibleAvatar responsible={message.responsible} /> : <span className="board-person-avatar board-person-avatar--small"><IconUser size={13} /></span>}
                          <span>{message.responsible?.name || "Sem responsável"}</span>
                        </button>
                        <button className={`board-card__deadline is-${deadline.tone}`} type="button" onClick={() => openDeadlineModal(message)}>
                          <IconCalendar size={13} aria-hidden="true" /> {deadline.label}
                        </button>
                        <button
                          className={`board-card__complete${message.stage === "done" ? " is-done" : ""}`}
                          type="button"
                          aria-label={message.stage === "done" ? "Reabrir conversa" : "Marcar como concluída"}
                          title={message.stage === "done" ? "Reabrir conversa" : "Marcar como concluída"}
                          onClick={() => void updateMessage(message.id, { stage: message.stage === "done" ? "inbox" : "done" }, message.stage === "done" ? "Conversa reaberta." : "Conversa concluída.")}
                        >
                          <IconCheck size={15} />
                        </button>
                        <button
                          className="board-card__menu"
                          type="button"
                          aria-label="Mais opções"
                          onClick={(event) => {
                            const rect = event.currentTarget.getBoundingClientRect();
                            openMenu(message.id, rect.right - 230, rect.bottom + 6);
                          }}
                        >
                          <IconDots size={17} aria-hidden="true" />
                        </button>
                      </footer>
                    </article>
                  );
                })}
                {columns[stage].length === 0 ? (
                  <p className="board-column__empty">Arraste uma conversa para esta etapa.</p>
                ) : null}
              </div>
            </section>
          ))}
        </div>
      </div>

      {menu && menuMessage ? (
        <div className="board-menu-layer" onMouseDown={() => setMenu(null)} onContextMenu={(event) => { event.preventDefault(); setMenu(null); }}>
          <div className="board-card-menu" style={{ left: menu.x, top: menu.y }} role="menu" onMouseDown={(event) => event.stopPropagation()}>
            <button type="button" role="menuitem" onClick={() => { setMenu(null); onOpenMessage(menuMessage.id); }}><IconMailOpened size={16} /> Abrir em tela completa</button>
            <button type="button" role="menuitem" onClick={() => openResponsibleModal(menuMessage.id)}><IconUser size={16} /> Alterar responsável</button>
            <button type="button" role="menuitem" onClick={() => openDeadlineModal(menuMessage)}><IconCalendar size={16} /> Definir prazo</button>
            <button type="button" role="menuitem" onClick={() => openTagsModal(menuMessage)}><IconTag size={16} /> Editar tags</button>
            <button type="button" role="menuitem" onClick={() => { setMenu(null); void updateMessage(menuMessage.id, { stage: menuMessage.stage === "done" ? "inbox" : "done" }, menuMessage.stage === "done" ? "Conversa reaberta." : "Conversa concluída."); }}>
              <IconCircleCheck size={16} /> {menuMessage.stage === "done" ? "Reabrir conversa" : "Marcar como concluída"}
            </button>
            <div className="board-card-menu__section"><span>Mover para</span>{STAGES.map((stage) => (
              <button className={menuMessage.stage === stage ? "is-active" : ""} type="button" key={stage} onClick={() => { setMenu(null); void moveMessage(menuMessage.id, { stage, index: columns[stage].length }); }}>
                {stageLabels[stage]} {menuMessage.stage === stage ? <IconCheck size={13} /> : null}
              </button>
            ))}</div>
            <div className="board-card-menu__row">
              <button type="button" onClick={() => void moveRelative(menuMessage, "top")}>Topo</button>
              <button type="button" onClick={() => void moveRelative(menuMessage, "up")}>Subir</button>
              <button type="button" onClick={() => void moveRelative(menuMessage, "down")}>Descer</button>
            </div>
          </div>
        </div>
      ) : null}

      {modal === "responsible" ? (
        <BoardDialog labelledBy="responsible-dialog-title" onClose={closeModal}>
          <header className="board-dialog__header">
            <div><span>Equipe do quadro</span><h2 id="responsible-dialog-title">{activeMessage ? "Definir responsável" : "Gerenciar responsáveis"}</h2><p>Este cadastro é independente dos usuários e caixas de e-mail.</p></div>
            <button type="button" onClick={closeModal} aria-label="Fechar"><IconX size={19} /></button>
          </header>
          {editingResponsible !== null || responsibleName ? (
            <div className="board-editor-form">
              <div className="board-editor-form__avatar">
                {responsibleAvatarPreview ? <img src={responsibleAvatarPreview} alt="Prévia da foto" /> : <span>{initials(responsibleName)}</span>}
                <label><IconPhoto size={15} /> Escolher foto<input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => {
                  const file = event.currentTarget.files?.[0];
                  if (!file) return;
                  if (file.size > 1_000_000) { onToast("A foto deve ter no máximo 1 MB."); return; }
                  void fileToAvatar(file).then(({ avatar, preview }) => {
                    setResponsibleAvatar(avatar);
                    setResponsibleAvatarPreview(preview);
                    setRemoveResponsibleAvatar(false);
                  }).catch((caught) => onToast(caught instanceof Error ? caught.message : "Não foi possível ler a foto."));
                }} /></label>
              </div>
              <label className="board-field"><span>Nome</span><input autoFocus maxLength={80} value={responsibleName} onInput={(event) => setResponsibleName(event.currentTarget.value)} placeholder="Nome do responsável" /></label>
              {responsibleAvatarPreview ? <button className="board-text-action" type="button" onClick={() => { setResponsibleAvatar(undefined); setResponsibleAvatarPreview(null); setRemoveResponsibleAvatar(true); }}>Remover foto</button> : null}
              <footer><button className="button button--secondary" type="button" onClick={() => { setEditingResponsible(null); setResponsibleName(""); setResponsibleAvatar(undefined); setResponsibleAvatarPreview(null); }}>Cancelar</button><button className="button button--primary" type="button" disabled={busy === "responsible" || !responsibleName.trim()} onClick={() => void saveResponsible()}>{busy === "responsible" ? "Salvando..." : editingResponsible ? "Salvar alterações" : "Criar responsável"}</button></footer>
            </div>
          ) : (
            <>
              {activeMessage && !managingResponsibles ? (
                <div className="board-dialog__search"><IconSearch size={17} /><input autoFocus type="search" value={responsibleQuery} onInput={(event) => setResponsibleQuery(event.currentTarget.value)} placeholder="Buscar ou criar responsável" /></div>
              ) : null}
              <div className="board-option-list">
                {activeMessage && !managingResponsibles ? <button className={!activeMessage.responsible ? "is-selected" : ""} type="button" onClick={() => void assignResponsible(null)}><span className="board-person-avatar board-person-avatar--large"><IconUser size={17} /></span><span><strong>Sem responsável</strong><small>Deixar a conversa disponível para a equipe</small></span>{!activeMessage.responsible ? <IconCheck size={17} /> : null}</button> : null}
                {filteredResponsibles.map((responsible) => (
                  <div className="board-managed-option" key={responsible.id}>
                    <button type="button" className={activeMessage?.responsible?.id === responsible.id ? "is-selected" : ""} onClick={() => activeMessage && !managingResponsibles ? void assignResponsible(responsible.id) : undefined}>
                      <ResponsibleAvatar responsible={responsible} size="large" /><span><strong>{responsible.name}</strong><small>Responsável do quadro</small></span>{activeMessage?.responsible?.id === responsible.id ? <IconCheck size={17} /> : null}
                    </button>
                    {managingResponsibles ? <span className="board-managed-option__actions"><button type="button" aria-label={`Editar ${responsible.name}`} onClick={() => startResponsibleForm(responsible, false)}><IconEdit size={15} /></button><button type="button" aria-label={`Excluir ${responsible.name}`} onClick={() => setConfirmResponsibleDelete(responsible.id)}><IconTrash size={15} /></button></span> : null}
                    {confirmResponsibleDelete === responsible.id ? <div className="board-inline-confirm"><span>Remover {responsible.name}? As conversas ficarão sem responsável.</span><button type="button" onClick={() => setConfirmResponsibleDelete(null)}>Cancelar</button><button type="button" disabled={busy === `responsible:${responsible.id}`} onClick={() => void deleteResponsible(responsible)}>Remover</button></div> : null}
                  </div>
                ))}
                {canCreateResponsible && !managingResponsibles ? <button className="board-option-list__create" type="button" onClick={() => startResponsibleForm(null, true)}><span><IconPlus size={17} /></span><span><strong>Criar “{responsibleQuery.trim()}”</strong><small>Cadastrar e definir nesta conversa</small></span></button> : null}
                {config.responsibles.length === 0 && !canCreateResponsible ? <p className="board-dialog__empty">Nenhum responsável cadastrado.</p> : null}
              </div>
              <footer className="board-dialog__footer">
                <button className="button button--secondary" type="button" onClick={() => { setManagingResponsibles((current) => !current); setResponsibleQuery(""); }}><IconUsers size={16} /> {managingResponsibles ? "Voltar à seleção" : "Gerenciar responsáveis"}</button>
                {managingResponsibles ? <button className="button button--primary" type="button" onClick={() => startResponsibleForm(null, false)}><IconPlus size={16} /> Novo responsável</button> : null}
              </footer>
            </>
          )}
        </BoardDialog>
      ) : null}

      {modal === "deadline" && activeMessage ? (
        <BoardDialog labelledBy="deadline-dialog-title" onClose={closeModal}>
          <header className="board-dialog__header"><div><span>Próxima ação</span><h2 id="deadline-dialog-title">Definir prazo</h2><p>O card muda de cor conforme a proximidade do vencimento.</p></div><button type="button" onClick={closeModal} aria-label="Fechar"><IconX size={19} /></button></header>
          <div className="board-deadline-editor">
            <label className="board-field"><span>Data do prazo</span><input autoFocus type="date" value={dueAt} min="2000-01-01" max="2100-12-31" onInput={(event) => setDueAt(event.currentTarget.value)} /></label>
            <div className="board-deadline-editor__quick" aria-label="Prazos rápidos"><button type="button" onClick={() => setDueAt(shiftDate(0))}>Hoje</button><button type="button" onClick={() => setDueAt(shiftDate(1))}>Amanhã</button><button type="button" onClick={() => setDueAt(shiftDate(3))}>Em 3 dias</button><button type="button" onClick={() => setDueAt(shiftDate(7))}>Em 1 semana</button></div>
            <div className="board-deadline-legend"><span className="is-safe">No prazo</span><span className="is-warning">Ontem ou hoje</span><span className="is-late">Vencido</span><span className="is-done">Concluído</span></div>
          </div>
          <footer className="board-dialog__footer board-dialog__footer--spread">
            <button className="button button--secondary" type="button" onClick={() => { setDueAt(""); void updateMessage(activeMessage.id, { dueAt: null }, "Prazo removido.").then((updated) => { if (updated) closeModal(); }); }}>Remover prazo</button>
            <div><button className="button button--secondary" type="button" onClick={() => void updateMessage(activeMessage.id, { stage: activeMessage.stage === "done" ? "inbox" : "done" }, activeMessage.stage === "done" ? "Conversa reaberta." : "Conversa concluída.").then((updated) => { if (updated) closeModal(); })}><IconCircleCheck size={16} /> {activeMessage.stage === "done" ? "Reabrir" : "Concluir"}</button><button className="button button--primary" type="button" disabled={!dueAt || Boolean(busy)} onClick={() => void updateMessage(activeMessage.id, { dueAt }, "Prazo atualizado.").then((updated) => { if (updated) closeModal(); })}>Salvar prazo</button></div>
          </footer>
        </BoardDialog>
      ) : null}

      {modal === "tags" && activeMessage ? (
        <BoardDialog labelledBy="tags-dialog-title" onClose={closeModal}>
          <header className="board-dialog__header"><div><span>Organização visual</span><h2 id="tags-dialog-title">Tags da conversa</h2><p>Selecione, crie e gerencie as tags do quadro.</p></div><button type="button" onClick={closeModal} aria-label="Fechar"><IconX size={19} /></button></header>
          {editingTag || tagName ? (
            <div className="board-editor-form">
              <label className="board-field"><span>Nome da tag</span><input autoFocus maxLength={32} value={tagName} onInput={(event) => setTagName(event.currentTarget.value)} placeholder="Ex.: Financeiro" /></label>
              <fieldset className="board-color-picker"><legend>Cor</legend>{tagColors.map((color) => <button className={`board-color board-color--${color}${tagColor === color ? " is-selected" : ""}`} type="button" key={color} onClick={() => setTagColor(color)} aria-label={`Usar cor ${color}`}><IconCheck size={12} /></button>)}</fieldset>
              <footer><button className="button button--secondary" type="button" onClick={() => { setEditingTag(null); setTagName(""); setTagColor("gray"); }}>Cancelar</button><button className="button button--primary" type="button" disabled={!tagName.trim() || busy === "tag"} onClick={() => void saveTag()}>{busy === "tag" ? "Salvando..." : editingTag ? "Salvar alterações" : "Criar tag"}</button></footer>
            </div>
          ) : (
            <>
              <div className="board-tag-manager">
                {config.tags.map((tag) => (
                  <div className="board-tag-option" key={tag.id}>
                    <button type="button" className={selectedTagIds.includes(tag.id) ? "is-selected" : ""} onClick={() => setSelectedTagIds((current) => current.includes(tag.id) ? current.filter((id) => id !== tag.id) : [...current, tag.id])}><span className={`board-tag board-tag--${tag.color}`}>{tag.name}</span>{selectedTagIds.includes(tag.id) ? <IconCheck size={16} /> : null}</button>
                    <span><button type="button" aria-label={`Editar ${tag.name}`} onClick={() => { setEditingTag(tag); setTagName(tag.name); setTagColor(tag.color); }}><IconEdit size={15} /></button><button type="button" aria-label={`Excluir ${tag.name}`} onClick={() => setConfirmTagDelete(tag.id)}><IconTrash size={15} /></button></span>
                    {confirmTagDelete === tag.id ? <div className="board-inline-confirm"><span>Excluir a tag {tag.name} de todas as conversas?</span><button type="button" onClick={() => setConfirmTagDelete(null)}>Cancelar</button><button type="button" disabled={busy === `tag:${tag.id}`} onClick={() => void deleteTag(tag)}>Excluir</button></div> : null}
                  </div>
                ))}
                {config.tags.length === 0 ? <p className="board-dialog__empty">Nenhuma tag criada ainda.</p> : null}
              </div>
              <footer className="board-dialog__footer board-dialog__footer--spread"><button className="button button--secondary" type="button" onClick={() => { setEditingTag(null); setTagName("Nova tag"); setTagColor("gray"); }}><IconPlus size={16} /> Criar tag</button><button className="button button--primary" type="button" disabled={Boolean(busy)} onClick={() => void updateMessage(activeMessage.id, { tagIds: selectedTagIds }, "Tags atualizadas.").then((updated) => { if (updated) closeModal(); })}>Salvar seleção</button></footer>
            </>
          )}
        </BoardDialog>
      ) : null}
    </section>
  );
}
